import React, {useEffect, useState, useRef} from 'react';
import {Link} from "react-router-dom";
import Modal from 'react-bootstrap/Modal';
import Button from "react-bootstrap/Button";
import LeadForm from "./LeadForm"
import InputPhoneNumber from "../phoneVerification/InputPhoneNumber";
import InputCode from "../phoneVerification/InputCode";
import UserDetails from "./UserDetails";
import SmarterQuestions from "./SmarterQuestions";
import AdditionalStep from "./AdditionalStep";
import LeadConfirmation from "./LeadConfirmation";
import { objectFilter, autoDownload, parameterize } from '../../../javascripts/utils';
import ModalClose from "../modals/ModalClose";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { LINK_CLASSES } from "../../constants/stylingConstants";
import ResponseModal from "../modals/ResponseModal";
import PeerTalkForm from "../peerTalk/PeerTalkForm";
import {COMPANY_NAME} from "../../constants/constants";
import { captureSplitTestGoal1, captureSplitTestGoal2 } from 'javascripts/split_test_campaigns.js';

function LeadGen(props) {

    let {
        token,
        gdpr,
        additionalStep=false,
        smarterQuestions={},
        showPhone,
        showCountry,
        emailOnly,
        cta,
        reportTopic,
        buttonOnly,
        buttonVariant,
        buttonSize='md',
        textOnly,
        externalToggle=false,
        toggleLeadGen=false,
        modalHeader,
        modalSubheader,
        currentUser={ first_name: '', last_name: '', phone: '', email: '', job_function: '', company_name: '', country: '' },
        sendReport,
        entryPage,
        leadCode,
        countries,
        gaReferrer,
        gaClientId,
        hiddenTags=[],
        vendor=null,
        reCaptchaKey,
        gtmEventRegion,
        gtmEventElement,
        directReportUrl = null,
        directReportDownload = false,
        withConfirmationScreen = true,
        confirmationParams={},
        toggleIcon=false,
        updateRoot=false,
        updateCurrentUser=()=>{},
        afterLeadCreation=()=>{},
        actionParams={},
        extraLeadData={},
        textLinkClasses="",
        includePeerTalk=false,
        redirect=true,
        modalOverlay=false
    } = props;

    const [verifyPhone, setVerifyPhone] = useState(false);
    const [verifyCode, setVerifyCode] = useState(false);
    const [userSignedIn, setUserSignedIn] = useState(currentUser?.id);
    const [redirectPath, setRedirectPath] = useState('/');
    const [twilioError, setTwilioError] = useState('');
    const [isLandline, setIsLandline] = useState(false);
    const [buttonCta, setButtonCta] = useState(cta);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [showUserDetails, setShowUserDetails] = useState(false);
    const [phoneConfirmed, setPhoneConfirmed] = useState(false);
    const [additionalLeadData, setAdditionalLeadData] = useState({});
    const redirectPathRef = useRef();
    const userRef = useRef(currentUser);
    const leadDataRef = useRef();
    redirectPathRef.current = redirectPath;
    leadDataRef.current = additionalLeadData;

    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }

    const updateLandline = (bool) => {
        setIsLandline(bool);
    }

    // Render modal components

    const renderPeerTalk = () => {
        return (
            <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
                <PeerTalkForm
                    productId={actionParams.product.id}
                    productName={actionParams.product.name}
                    categoryId={actionParams.product.category.id}
                    isCancelling={false}
                    questions={actionParams.smarter_questions}
                    answeredQuestions={actionParams.product.smarter_questions}
                    gdpr={gdpr}
                    selectedExpert={actionParams.selected}
                    currentUser={currentUser}
                    token={token}
                    renderAsLeadGen={true}
                    hiddenTags={hiddenTags}
                    onLeadCreate={onLeadCreate}
                    showEmail={false}
                    onNextModal={onNextModal}
                />
            </GoogleReCaptchaProvider>
        )
    }

    const renderLeadForm = () => {
        return (
            <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
                <LeadForm
                    token={token}
                    gdpr={gdpr}
                    showPhone={showPhone}
                    showCountry={showCountry}
                    emailOnly={emailOnly}
                    buttonOnly={buttonOnly || textOnly}
                    cta={cta}
                    currentUser={userRef.current}
                    entryPage={entryPage}
                    leadCode={leadCode}
                    countries={countries}
                    gaReferrer={gaReferrer}
                    gaClientId={gaClientId}
                    hiddenTags={hiddenTags}
                    additionalLeadData={leadDataRef.current}
                    vendor={vendor}
                    userSignedIn={userSignedIn}
                    modalHeader={modalHeader}
                    modalSubheader={modalSubheader}
                    onLeadCreate={onLeadCreate}>
                    
                </LeadForm>
            </GoogleReCaptchaProvider>
        )
    }

    const renderInputPhoneNumber = () => {
        return (
            <InputPhoneNumber
                token={token}
                showPhone={showPhone}
                currentUser={userRef.current}
                emailOnly={emailOnly}
                twilioError={twilioError}
                isLandline={isLandline}
                updateLandline={updateLandline}
                onNextModal={onNextModal}
            />
        )
    }

    const renderInputCode = () => {
        return (
            <InputCode
                token={token}
                currentUser={userRef.current}
                onEditPhone={onEditPhone}
                twilioError={twilioError}
                isLandline={isLandline}
                onNextModal={onNextModal}
            />
        )
    }

    const renderUserDetails = () => {
        return (
            <UserDetails
                token={token}
                currentUser={userRef.current}
                userSignedIn={userSignedIn}
                onNextModal={onNextModal}
            />
        )
    }

    const renderSmarterQuestions = () => {
        return (
            <SmarterQuestions />
        )
    }

    const renderAdditionalStep = () => {
        return (
            <AdditionalStep />
        )
    }

    const renderLeadConfirmation = () => {
        let header = confirmationParams?.header || 'The report was sent to your email';
        let subHeader = confirmationParams?.subHeader || `Thank you for your interest in ${COMPANY_NAME}'s user reviews ${reportTopic}.`;
        return (
            <LeadConfirmation
                redirectPath={redirectPathRef.current}
                reportTopic={reportTopic}
                peerTalkConfirm={includePeerTalk}
                header={header}
                subHeader={subHeader}
                includeRedirect={redirect}
                includeFeatures={!includePeerTalk}
            />
        )
    }

    // Modal step functionality

    const stepComponents = {
        peerTalk: renderPeerTalk,
        leadForm: renderLeadForm,
        inputPhoneNumber: renderInputPhoneNumber,
        inputCode: renderInputCode,
        userDetails: renderUserDetails,
        smarterQuestions: renderSmarterQuestions, // disabled
        additionalStep: renderAdditionalStep,      // disabled
        leadConfirmation: renderLeadConfirmation,
    };

    const stepComponentsKeys = Object.keys(stepComponents);

    const getSteps = (currentSteps=[]) => {
        let updatedSteps = new Set(currentSteps);

        const addStep = (stepId, condition) => {
            if (condition) {
                updatedSteps.add(stepId);
            }
        }

        addStep('peerTalk', includePeerTalk);
        addStep('leadForm', (buttonOnly || textOnly || externalToggle) && !currentUser?.id);
        addStep('inputPhoneNumber', verifyPhone);
        addStep('inputCode', verifyCode);
        addStep('userDetails', showUserDetails);
        addStep('smarterQuestions', smarterQuestions && 0); // disabled
        addStep('additionalStep', additionalStep && 0); // disabled
        addStep('leadConfirmation', withConfirmationScreen);

        return Array.from(updatedSteps).sort((a, b) => {
            return stepComponentsKeys.indexOf(a) - stepComponentsKeys.indexOf(b);
        });
    };

    const [showModal, setShowModal] = useState(false);
    const [modalSteps, setModalSteps] = useState(() => getSteps());
    const [activeStep, setActiveStep] = useState(0);
    const [showReportDownload, setShowReportDownload] = useState(false);
    const [reportGenerationFailure, setReportGenerationFailure] = useState(false);
    const [leadCreationData, setLeadCreationData] = useState({});
    const stepRef = useRef();
    const modalRef = useRef();

    stepRef.current = activeStep;

    useEffect(() => {
        if(externalToggle && toggleLeadGen && !showModal) {
            setActiveStep(0);
            onLeadGenToggle()
        }
    }, [toggleLeadGen, leadCode]);

    useEffect(() => {
        if(Object.keys(extraLeadData).length) {
            setAdditionalLeadData(extraLeadData);
            leadDataRef.current = extraLeadData;
        }
    }, [extraLeadData]);

    useEffect(() => {
        if(verifyPhone || verifyCode || showUserDetails) {
            setModalSteps(() => getSteps(modalSteps));
        }
    }, [verifyPhone, verifyCode, showUserDetails]);

    useEffect(() => {
        let steps = getSteps(modalSteps);
        // no more steps to show, but modal is still open (because onModalClose() was not triggered by anything), 
        // so we need to deal with it by either closing it or redirecting
        if (showModal && activeStep >= steps.length) {
            setShowModal(false);
            if (updateRoot && userRef.current) afterLeadCreation(leadCode, actionParams, leadCreationData);
            modalRef.current = false;
            if (directReportDownload && directReportUrl) {
                // window.open(directReportUrl, '_blank');
                setShowReportDownload(true);
                if (userRef.current.verified_email) autoDownload(directReportUrl, `${parameterize(reportTopic)}.pdf`);
            } else if (!withConfirmationScreen && redirect) { // if no confirmation screen, we need to manually trigger the redirect
                window.location.href = redirectPathRef.current;
            } else if (directReportDownload) { // we were expecting directReportUrl but there's none
                setReportGenerationFailure(true);
            }
        } else if (!showModal && phoneConfirmed) {
            setVerifyPhone(false);
            setVerifyCode(false);
        }
    }, [showModal, activeStep]);

    const onModalClose = () => {
        if(activeStep === 0 || updateRoot) {
            setShowModal(false);
            modalRef.current = false;
            if(updateRoot && userRef.current) afterLeadCreation(leadCode, actionParams, leadCreationData);
            if (directReportDownload && directReportUrl && userRef.current) {
                setShowReportDownload(true);
                if(userRef.current.verified_email) autoDownload(directReportUrl, `${parameterize(reportTopic)}.pdf`);
            }
        } else window.location.href = redirectPathRef.current;
    }

    const renderModal = () => {
        return (
            <>
                <div key={`backdrop-${leadCode}`}
                     className={modalOverlay && showModal ? `modal-backdrop fade show` : ''}
                     style={{
                         zIndex: modalOverlay ? 2000 : 'initial',
                     }}
                />
                <Modal
                    key={leadCode}
                    show={showModal}
                    backdrop={!modalOverlay}
                    bsPrefix={modalOverlay ? 'modal-over-modal': ''}
                    keyboard={false}
                    centered
                    fullscreen={'sm-down'}
                    enforceFocus={false}
                    className={'justify-content-center'}
                    scrollable={true}
                    dialogClassName={'modal-md'}
                    contentClassName={'modal-lead-sizing'}>
                    <ModalClose onClose={onModalClose} />
                    {modalSteps[activeStep] && stepComponents[modalSteps[activeStep]]()}
                </Modal>
           </>
        )
    }

    // Event handlers

    const onLeadCreate = (data) => {

        if(redirect && data.redirect_path) {
            setRedirectPath(data.redirect_path);
        }

        if(data?.next_step === "report_was_sent_notification") {
            setActiveStep(modalSteps.length-1); // show last step
            setShowModal(true);
            modalRef.current = true;
            return;
        }

        if(data.user_id) {
            trackSplitTestGloal2(data.user?.email);
            setUserSignedIn(true);
            userRef.current = data?.user;
        }

        if(updateRoot) {
            setLeadCreationData(data);
        }

        setShowUserDetails(!!data.user_details_missing);

        if(data?.next_step === 'verify_phone') {
            if (data?.twilio_response?.skip_verification === false) {
                setVerifyPhone(true);
                setVerifyCode(true);
                setIsLandline(!!data?.twilio_response?.landline);
                setTwilioError(data?.twilio_response?.error);
            }
        }

        if(modalRef.current) {
            // the modal is open, move to the next step
            setActiveStep(stepRef.current + 1);
        } else {
            setActiveStep(0);
            setShowModal(true);
            modalRef.current = true;
        }
    }

    const onNextModal = (data={}) => {
        // data can pass in user.phone to reset phone number
        // data can pass in skip, which tells us how many modals to skip ahead
        if(data?.user?.phone) {
            userRef.current = { ...userRef.current, phone: data.user.phone };
            if(updateRoot) updateCurrentUser(userRef.current);
        }

        if(redirect && data?.redirect_path) {
            setRedirectPath(data.redirect_path);
        }

        if(data?.phoneConfirmed){
            setPhoneConfirmed(true);
        }

        if(data?.additionalLeadData) {
            setAdditionalLeadData(data.additionalLeadData);
            leadDataRef.current = data.additionalLeadData;
        }

        // Referencing states within a callback function: stepRef represents activeStep.
        // See https://stackoverflow.com/questions/57847594/react-hooks-accessing-up-to-date-state-from-within-a-callback
        setActiveStep(stepRef.current + (data?.skip || 1));
    }

    const onEditPhone = (e) => {
        // setVerifyPhone(true);
        // setActiveStep((buttonOnly || textOnly) ? 1 : 0);
        setActiveStep(Math.max(stepRef.current - 1, 0));
    }

    const trackSplitTestGoal1 = () => {
        // don't increment goal1 if on landing page (it's already tracked)
        if (window.location.pathname.includes('/landing/')) { return; }

        captureSplitTestGoal1();
    }

    const trackSplitTestGloal2 = (email) => {
        const queryParams = window.dataLayer[0].splitTestQueryParams;
        const url = new URL(window.location.href);
        const searchParams = new URLSearchParams(queryParams);
        url.search = searchParams; // we do not bother with site wide params because all conversions are accepted for that type of campaigns
    
        captureSplitTestGoal2(email, url.href);
    }

    const onLeadGenToggle = async() => {
        let leadTags = objectFilter(hiddenTags, ([k,_v]) => k.includes('lead'));
        let leadData = Object.keys(leadTags).map(k => {
            let key = k.replace('lead[','').replace(']','');
            return { [key]: leadTags[k] }
        });

        if(!userRef.current?.id || includePeerTalk) {
            trackSplitTestGoal1();
            setShowModal(true);
            modalRef.current = true;
        } else if(sendReport) {
            setButtonCta('Submitting...');
            setButtonDisabled(true);
            let leadParams =  {
                entry_page: entryPage,
                lead_code: leadCode,
                vendor_id: (vendor?.opt_in && vendor?.id),
                ga_client_id: gaClientId,
                ...Object.assign({}, ...leadData)
            };
            let data = { lead: leadParams, user: currentUser, authenticity_token: token, ...hiddenTags };

            await fetch('/landing/create_lead', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(data)
            }).then(function(response) {
                return response.json();
            }).then(function(jsonResponse) {
                if(jsonResponse.success) {
                    const updatedUser = {...jsonResponse.user, ...data.user}; // include user fields from jsonResponse
                    onLeadCreate({...jsonResponse, ...data, user: updatedUser});
                }
                setButtonDisabled(false);
            });
            setButtonCta(cta);
        }
    }

    const onCloseReportModal = (_) => {
        setShowReportDownload(false);
    }

    const renderReportModal = () => {
        if(showReportDownload) {
            let message     = '';
            let color            = '';
            let txtColor       = '';
            let sendJSX      = false;
            if(userRef.current.verified_email) {
                let linkClasses = 'text-blue-link hover-blue-hover text-decoration-underline';
                let url = '/peer-research/peer-reports';
                let link = updateRoot ? <Link to={url}
                                                     className={linkClasses}>your report library</Link> : <a href={url} className={linkClasses}>your report library</a>
                message =  (
                    <>Your report is being downloaded and will be added to {link}.</>
                );
                color = 'success';
                txtColor = 'success';
                sendJSX = updateRoot;
            } else {
                message = 'We sent you an email with a link to the report. Please check your inbox.';
                color = 'primary';
                txtColor = 'secondary';
            }

            return (
                <ResponseModal
                    modalSize={'sm'}
                    bgColor={`soft-${color}`}
                    textColor={txtColor}
                    showIcon={false}
                    message={message}
                    show={showReportDownload}
                    centered={false}
                    textAlignment={'start'}
                    btnClose={false}
                    borderColor={color}
                    dialogClasses={'modal-bottom-notice'}
                    showBackdrop={false}
                    btnColor={color}
                    handleShow={onCloseReportModal}
                    JSXContent={sendJSX}
                />
            )
        }
    }

    const renderReportGenerationFailureModal = () => {
        if(reportGenerationFailure) {
            let message = `There was a problem generating your report, please try again later. <br />If this issue persists, please contact PeerSpot support`;

            return (
                <ResponseModal
                    modalSize={'sm'}
                    bgColor={'soft-danger'}
                    textColor={'danger'}
                    showIcon={false}
                    message={message}
                    show={!!reportGenerationFailure}
                    centered={false}
                    textAlignment={'start'}
                    btnClose={false}
                    borderColor={'danger'}
                    dialogClasses={'modal-bottom-notice'}
                    showBackdrop={false}
                    btnColor={'danger'}
                    handleShow={setReportGenerationFailure}
                />
            )
        }
    }

    const renderInit = () => {
        if(externalToggle) return;
        if(buttonOnly) {
            let icon = toggleIcon ? <i className={`fal fa-${toggleIcon} ps-1`}></i> : '';
            return (
                <Button data-gtm-event-region={gtmEventRegion} data-gtm-event-element={gtmEventElement} variant={buttonVariant} size={buttonSize} className={'btn-block px-1'} disabled={buttonDisabled} onClick={(e) => onLeadGenToggle()}>
                    {buttonCta}
                    {icon}
                </Button>
            )
        } else if(textOnly) {
            if(toggleIcon) {
                return (
                    <a className={LINK_CLASSES} onClick={(e) => onLeadGenToggle()} data-gtm-event-region={gtmEventRegion} data-gtm-event-element={gtmEventElement}><i className={`fas fa-${toggleIcon} pe-1`}></i> {cta}</a>
                )
            } else {
                return (
                    <a className={textLinkClasses} onClick={(e) => onLeadGenToggle()} data-gtm-event-region={gtmEventRegion} data-gtm-event-element={gtmEventElement}>{cta}</a>
                )
            }
            
        }
        return (
            <>{renderLeadForm()}</>
        )
    }

    return (
        <>
            {renderInit()}
            {renderModal()}
            {renderReportModal()}
            {renderReportGenerationFailureModal()}
        </>
    )

}

LeadGen.displayName = 'LeadGen';

export default LeadGen;